// src/admin/pages/Contacts.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Paper, Grid, Typography, TextField, Button, Snackbar, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions, Fab
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AddIcon from '@mui/icons-material/Add';
import EmailIcon from '@mui/icons-material/Email';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import CrudTable from '../components/CrudTable';
import {
    adminContacts,
    adminSendEmail,
    adminCreateContact,
    adminUpdateContact,
    adminDeleteContact
} from '../../utils/api';

// PDF helpers
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const ReadonlyTF = (props) => (
    <TextField {...props} fullWidth variant="outlined" InputProps={{ readOnly: true }} />
);

/* ---------------- Email dialog (mobile full-screen) ---------------- */
function EmailDialog({ open, onClose, name, email, onSent }) {
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => { if (open) { setSubject(''); setMessage(''); } }, [open, name, email]);

    const send = async () => {
        if (!email) return alert('No email for this contact.');
        setSending(true);
        try {
            const res = await adminSendEmail({ to: email, subject, message });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data?.ok) throw new Error(data?.error || 'Failed to send message.');
            onSent?.(); onClose?.();
        } catch (e) { alert(e.message || 'Failed to send message.'); }
        finally { setSending(false); }
    };

    return (
        <Dialog
            open={open}
            fullScreen={fullScreen}
            onClose={(e, reason) => { if (reason === 'backdropClick' || reason === 'escapeKeyDown') return; onClose?.(); }}
            disableEscapeKeyDown
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle>Send Message</DialogTitle>
            <DialogContent dividers sx={{ display: 'grid', gap: 2 }}>
                <ReadonlyTF label="Name"  value={name || ''} />
                <ReadonlyTF label="Email" value={email || ''} />
                <TextField label="Subject" value={subject} onChange={(e)=>setSubject(e.target.value)} fullWidth />
                <TextField label="Message" value={message} onChange={(e)=>setMessage(e.target.value)} fullWidth multiline minRows={8} />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={send} disabled={sending || !subject || !message}>Send</Button>
            </DialogActions>
        </Dialog>
    );
}

/* ---------------- Add/Edit Contact dialog (mobile full-screen) ---------------- */
function ContactDialog({ open, mode, initial, onCancel, onSave }) {
    const [form, setForm] = useState(initial || {});
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    // reset the form every time the dialog opens
    useEffect(() => { if (open) setForm(initial || {}); }, [open, initial]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    return (
        <Dialog
            open={open}
            fullScreen={fullScreen}
            // no close icon; disable backdrop + Esc to enforce using Cancel/Save
            onClose={(e, reason) => { if (reason === 'backdropClick' || reason === 'escapeKeyDown') return; onCancel?.(); }}
            disableEscapeKeyDown
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle>{mode === 'edit' ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
            <DialogContent dividers sx={{ display: 'grid', gap: 1.5 }}>
                {/* one label per row, mobile friendly */}
                <TextField label="Name"        value={form.name || ''}        onChange={set('name')}        fullWidth />
                <TextField label="Email"       value={form.email || ''}       onChange={set('email')}       type="email" fullWidth />
                <TextField label="Phone"       value={form.phone || ''}       onChange={set('phone')}       fullWidth />
                <TextField label="Occupation"  value={form.occupation || ''}  onChange={set('occupation')}  fullWidth />
                <TextField label="Notes"       value={form.notes || ''}       onChange={set('notes')}       fullWidth multiline minRows={6} />
            </DialogContent>

            {/* fixed bottom-right actions (sticky so they stay visible while scrolling) */}
            <DialogActions
                sx={{
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 1,
                    bgcolor: 'background.paper',
                    justifyContent: 'flex-end'
                }}
            >
                <Button onClick={onCancel}>Cancel</Button>
                <Button variant="contained" onClick={() => onSave(form)}>Save</Button>
            </DialogActions>
        </Dialog>
    );
}

/* ---------------- Delete confirmation dialog (mobile full-screen) ---------------- */
function DeleteConfirmDialog({ open, name, onCancel, onConfirm }) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    return (
        <Dialog
            open={open}
            fullScreen={fullScreen}
            onClose={(e, reason) => { if (reason === 'backdropClick' || reason === 'escapeKeyDown') return; onCancel?.(); }}
            disableEscapeKeyDown
            fullWidth
            maxWidth="xs"
        >
            <DialogTitle>Delete Contact</DialogTitle>
            <DialogContent dividers>
                Are you sure you want to delete <strong>{name || 'this contact'}</strong>?
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'flex-end' }}>
                <Button onClick={onCancel}>Cancel</Button>
                <Button color="error" variant="contained" onClick={onConfirm}>Delete</Button>
            </DialogActions>
        </Dialog>
    );
}

/* ---------------- Mobile filter popup ---------------- */
function FilterDialog({ open, initial, onSearch, onClear, onCancel }) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [local, setLocal] = useState(initial);

    useEffect(() => { if (open) setLocal(initial); }, [open, initial]);

    const set = (k) => (e) => setLocal((f) => ({ ...f, [k]: e.target.value }));

    return (
        <Dialog open={open} fullWidth maxWidth="sm" fullScreen={fullScreen}
                onClose={(e, reason) => { if (reason === 'backdropClick' || reason === 'escapeKeyDown') return; onCancel?.(); }}
                disableEscapeKeyDown>
            <DialogTitle>Filter Contacts</DialogTitle>
            <DialogContent dividers sx={{ display: 'grid', gap: 1.25 }}>
                <TextField label="Name" value={local.q} onChange={set('q')} fullWidth />
                <TextField label="Email" value={local.email} onChange={set('email')} fullWidth />
                <TextField label="Occupation" value={local.occupation} onChange={set('occupation')} fullWidth />
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'flex-end' }}>
                <Button onClick={() => { onClear?.(); }}>Clear</Button>
                <Button variant="contained" onClick={() => onSearch?.(local)}>Search</Button>
                <Button onClick={onCancel}>Cancel</Button>
            </DialogActions>
        </Dialog>
    );
}

/* ---------------- Page ---------------- */
export default function ContactsPage() {
    const [rows, setRows] = useState([]);
    const [filters, setFilters] = useState({ q: '', email: '', occupation: '' });
    const [filterDlgOpen, setFilterDlgOpen] = useState(false);

    // email dialog
    const [emailDlg, setEmailDlg] = useState({ open: false, name: '', email: '' });

    // add/edit dialog
    const [contactDlg, setContactDlg] = useState({ open: false, mode: 'add', initial: null });

    // delete dialog
    const [delDlg, setDelDlg] = useState({ open: false, row: null });

    // toasts
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
    const [snack, setSnack] = useState(false); // keep your existing "Message sent." snackbar

    const load = async () => {
        const data = await adminContacts();
        setRows(data.rows || []);
    };
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        const f = { ...filters };
        Object.keys(f).forEach(k => f[k] = String(f[k] || '').trim().toLowerCase());
        return rows.filter((r) => {
            const name = String(r.name || '').toLowerCase();
            if (f.q && !name.includes(f.q)) return false;
            if (f.email && !String(r.email || '').toLowerCase().includes(f.email)) return false;
            if (f.occupation && !String(r.occupation || '').toLowerCase().includes(f.occupation)) return false;
            return true;
        });
    }, [rows, filters]);

    const columns = [
        { key: 'name',        header: 'Name', nowrap: true },
        {
            key: 'email',       header: 'Email', nowrap: true,
            render: (v, r) => v ? (
                <Button size="small" variant="text" startIcon={<EmailIcon />}
                        onClick={(e)=>{ e.stopPropagation(); setEmailDlg({ open:true, name:r.name, email:v }); }}>
                    {v}
                </Button>
            ) : '—'
        },
        { key: 'phone',       header: 'Phone', nowrap: true },
        { key: 'occupation',  header: 'Occupation', nowrap: true },
        { key: 'notes',       header: 'Notes' },
    ];

    /* ---------- PDF Download ---------- */
    const handleDownloadPdf = () => {
        // Build a PDF with the current filtered rows and the same columns
        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'pt',
            format: 'a4',
        });

        const title = 'Contacts Report';
        const generatedAt = new Date().toLocaleString();

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(18);
        doc.text(title, 40, 40);

        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(80);
        doc.text(`Generated: ${generatedAt}`, 40, 60);
        doc.text(`Total contacts: ${filtered.length}`, 40, 76);

        const head = [columns.map(col => col.header)];
        const body = filtered.map(row =>
            columns.map(col => {
                const raw = row[col.key];
                const val = (raw === null || raw === undefined || raw === '') ? '—' : String(raw);
                // Give notes a bit of cleaner whitespace
                return col.key === 'notes' ? val.replace(/\s+/g, ' ').trim() : val;
            })
        );

        if (!body.length) {
            // No data – still create a simple PDF message
            doc.setTextColor(120);
            doc.text('No contacts match the current filters.', 40, 110);
            doc.save('contacts-report.pdf');
            return;
        }

        autoTable(doc, {
            head,
            body,
            startY: 96,
            styles: {
                fontSize: 9,
                cellPadding: 6,
                valign: 'middle',
                overflow: 'linebreak',
            },
            headStyles: {
                fillColor: [33, 150, 243], // soft blue
                textColor: 255,
                fontSize: 10,
                halign: 'left',
            },
            alternateRowStyles: {
                fillColor: [245, 245, 245], // light gray striping
            },
            bodyStyles: {
                textColor: 40,
            },
            columnStyles: {
                // Let "Notes" stretch more on the page
                [columns.findIndex(c => c.key === 'notes')]: { cellWidth: 'auto' },
            },
            margin: { top: 80, left: 40, right: 40, bottom: 40 },
        });

        doc.save('contacts-report.pdf');
    };

    /* ---------- actions ---------- */
    const startAdd  = () => setContactDlg({ open: true, mode: 'add',  initial: { name:'', email:'', phone:'', occupation:'', notes:'' } });
    const startEdit = (row) => setContactDlg({ open: true, mode: 'edit', initial: { ...row } });
    const startDelete = (row) => setDelDlg({ open: true, row });

    const saveContact = async (form) => {
        try {
            if (contactDlg.mode === 'edit') {
                const resp = await adminUpdateContact(contactDlg.initial.id, {
                    name: form.name?.trim() || '',
                    email: form.email?.trim() || '',
                    phone: form.phone?.trim() || '',
                    occupation: form.occupation?.trim() || '',
                    notes: form.notes ?? '',
                });
                if (!resp.ok) {
                    const data = await resp.json().catch(()=>({}));
                    throw new Error(Array.isArray(data?.error) ? data.error.join(', ') : (data?.error || 'Update failed'));
                }
                setToast({ open: true, message: 'Contact updated.', severity: 'success' });
            } else {
                const resp = await adminCreateContact({
                    name: form.name?.trim() || '',
                    email: form.email?.trim() || '',
                    phone: form.phone?.trim() || '',
                    occupation: form.occupation?.trim() || '',
                    notes: form.notes ?? '',
                });
                if (!resp.ok) {
                    const data = await resp.json().catch(()=>({}));
                    throw new Error(Array.isArray(data?.error) ? data.error.join(', ') : (data?.error || 'Create failed'));
                }
                setToast({ open: true, message: 'Contact added.', severity: 'success' });
            }
            setContactDlg({ open: false, mode: 'add', initial: null });
            await load();
        } catch (err) {
            alert(err.message || 'Save failed');
        }
    };

    const confirmDelete = async () => {
        const row = delDlg.row;
        if (!row) return;
        try {
            const resp = await adminDeleteContact(row.id);
            if (!resp.ok) throw new Error('Delete failed');
            setDelDlg({ open: false, row: null });
            setToast({ open: true, message: 'Contact deleted.', severity: 'success' });
            await load();
        } catch (err) {
            alert(err.message || 'Delete failed');
        }
    };

    return (
        <Box>
            {/* Sticky header with Add + filters + Download */}
            <Paper elevation={1} sx={{ position: 'sticky', top: 0, zIndex: 5, p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>Contacts</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        {/* Mobile filters button */}
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<FilterListIcon />}
                            onClick={() => setFilterDlgOpen(true)}
                            sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
                        >
                            Filters
                        </Button>

                        {/* Download button */}
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownloadPdf}
                        >
                            Download PDF
                        </Button>

                        {/* Desktop add button; mobile has FAB below */}
                        <Button
                            variant="contained"
                            onClick={startAdd}
                            sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                        >
                            Add Contact
                        </Button>
                    </Box>
                </Box>

                {/* Desktop filters */}
                <Grid container spacing={1} sx={{ display: { xs: 'none', sm: 'flex' } }}>
                    <Grid item xs={12} sm={4}>
                        <TextField size="small" label="Name" fullWidth
                                   value={filters.q} onChange={(e)=>setFilters({ ...filters, q: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField size="small" label="Email" fullWidth
                                   value={filters.email} onChange={(e)=>setFilters({ ...filters, email: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField size="small" label="Occupation" fullWidth
                                   value={filters.occupation} onChange={(e)=>setFilters({ ...filters, occupation: e.target.value })} />
                    </Grid>
                </Grid>
            </Paper>

            {/* Table (mobile -> auto card view via CrudTable responsive styles) */}
            <CrudTable
                ariaLabel="Contacts list"
                columns={columns}
                rows={filtered}
                onEdit={startEdit}
                onDelete={startDelete}
                height="calc(100dvh - 280px)"
            />

            {/* dialogs */}
            <ContactDialog
                open={contactDlg.open}
                mode={contactDlg.mode}
                initial={contactDlg.initial}
                onCancel={() => setContactDlg({ open: false, mode: 'add', initial: null })}
                onSave={saveContact}
            />

            <DeleteConfirmDialog
                open={delDlg.open}
                name={delDlg.row?.name}
                onCancel={() => setDelDlg({ open: false, row: null })}
                onConfirm={confirmDelete}
            />

            <EmailDialog
                open={emailDlg.open}
                name={emailDlg.name}
                email={emailDlg.email}
                onClose={()=>setEmailDlg({ open:false, name:'', email:'' })}
                onSent={()=>setSnack(true)}
            />

            {/* Mobile filter dialog */}
            <FilterDialog
                open={filterDlgOpen}
                initial={filters}
                onSearch={(vals) => { setFilters(vals); setFilterDlgOpen(false); }}
                onClear={() => { setFilters({ q: '', email: '', occupation: '' }); setFilterDlgOpen(false); }}
                onCancel={() => setFilterDlgOpen(false)}
            />

            {/* toast for add/edit/delete */}
            <Snackbar open={toast.open} autoHideDuration={2500} onClose={()=>setToast({ ...toast, open:false })}>
                <Alert severity={toast.severity} onClose={()=>setToast({ ...toast, open:false })}>{toast.message}</Alert>
            </Snackbar>

            {/* keep your existing "Message sent." snackbar */}
            <Snackbar open={snack} autoHideDuration={3000} onClose={()=>setSnack(false)}>
                <Alert severity="success" onClose={()=>setSnack(false)}>Message sent.</Alert>
            </Snackbar>

            {/* Mobile FAB for quick add */}
            <Fab
                color="primary"
                onClick={startAdd}
                sx={{ position: 'fixed', right: 16, bottom: 16, display: { xs: 'flex', sm: 'none' } }}
                aria-label="Add contact"
            >
                <AddIcon />
            </Fab>
        </Box>
    );
}
